import { breakEvenAge } from "./break-even";
import { getColaDefaultVerified } from "./cola";
import { getFraTableVerified, lookupFra } from "./fra";
import { benefitAtAge } from "./lifetime";
import { getBendPointsVerified, resolvePia } from "./pia";
import { identifyEarners, lowerEarnerEffectiveBenefit } from "./spousal";
import { survivorBenefit, survivorOptimizationHint } from "./survivor";
import type {
  CalculatorInputs,
  CalculatorResults,
  CoupleResults,
  PersonInputs,
  PersonResults,
} from "./types";

export const ENGINE_VERSION = "0.1.0";

function personResults(person: PersonInputs, colaRate: number): PersonResults {
  const birthYear = new Date(person.dob).getUTCFullYear();
  const fra = lookupFra(birthYear);
  const fraAgeYears = fra.years + fra.months / 12;
  const pia = resolvePia(person);

  const at62 = benefitAtAge({ person, claimAge: 62, colaRate });
  const atFra = benefitAtAge({ person, claimAge: fraAgeYears, colaRate });
  const at70 = benefitAtAge({ person, claimAge: 70, colaRate });
  const custom = benefitAtAge({ person, claimAge: person.claimAge, colaRate });

  return {
    fra,
    pia,
    benefits: { at62, atFra, at70, custom },
    breakEvens: {
      age62VsFra: breakEvenAge({ person, earlierClaimAge: 62, laterClaimAge: fraAgeYears, colaRate }),
      ageFraVs70: breakEvenAge({ person, earlierClaimAge: fraAgeYears, laterClaimAge: 70, colaRate }),
      age62Vs70: breakEvenAge({ person, earlierClaimAge: 62, laterClaimAge: 70, colaRate }),
    },
  };
}

function coupleResults(primary: PersonInputs, spouse: PersonInputs): CoupleResults {
  const { higher, lower } = identifyEarners(primary, spouse);
  const spousal = lowerEarnerEffectiveBenefit({
    lowerEarner: lower.person,
    higherEarnerPia: higher.pia,
  });
  const survivor = survivorBenefit({ higherEarner: higher.person });
  const hint = survivorOptimizationHint(higher.person.claimAge);
  return {
    spousalBenefitAtFra: spousal.source === "spousal" ? spousal.monthly : 0.5 * higher.pia,
    survivorBenefit: survivor,
    survivorOptimizationHint: hint,
  };
}

function buildRecommendation(primary: PersonResults, hasSpouse: boolean): string {
  const choices = [
    { label: "62", value: primary.benefits.at62.lifetimeCumulative, age: 62 },
    { label: "FRA", value: primary.benefits.atFra.lifetimeCumulative, age: primary.fra.years + primary.fra.months / 12 },
    { label: "70", value: primary.benefits.at70.lifetimeCumulative, age: 70 },
  ];
  choices.sort((a, b) => b.value - a.value);
  const best = choices[0];
  const ageText = best.age === Math.round(best.age) ? `${best.age}` : best.age.toFixed(1);
  const couples = hasSpouse
    ? " Couples planning amplifies this — coordinating the higher earner's claim age with survivor outcomes is one of the most powerful levers in household income planning."
    : "";
  return (
    `Based on the assumptions you entered, claiming at age ${ageText} produces the highest cumulative lifetime benefit ` +
    `over your stated longevity horizon. Social Security is the foundation of your Soon bucket — the guaranteed, ` +
    `inflation-adjusted income floor that lets the Later bucket stay invested for growth.` +
    couples +
    ` This is a starting point, not a recommendation — claim timing interacts with taxes, Medicare premiums, ` +
    `survivor planning, and your full retirement income picture. Use this as a prompt to work the numbers further.`
  );
}

/** Public API. Pure function. */
export function run(inputs: CalculatorInputs): CalculatorResults {
  const primary = personResults(inputs.primary, inputs.colaRate);
  const spouse = inputs.spouse ? personResults(inputs.spouse, inputs.colaRate) : undefined;
  const couple = inputs.spouse
    ? coupleResults(inputs.primary, inputs.spouse)
    : undefined;
  return {
    primary,
    spouse,
    couple,
    recommendation: buildRecommendation(primary, Boolean(inputs.spouse)),
    meta: {
      engineVersion: ENGINE_VERSION,
      fraTableVerified: getFraTableVerified(),
      bendPointsVerified: getBendPointsVerified(),
      colaDefaultVerified: getColaDefaultVerified(),
    },
  };
}

export { lookupFra, fraInMonths, getFraTableVerified } from "./fra";
export { computePiaFromAime, resolvePia, getBendPointsVerified } from "./pia";
export { applyClaimAdjustment } from "./claim-adjustment";
export { cumulativeWithCola, getDefaultColaRate, getColaDefaultVerified } from "./cola";
export { benefitAtAge } from "./lifetime";
export { breakEvenAge } from "./break-even";
export { spousalBenefit, identifyEarners, lowerEarnerEffectiveBenefit } from "./spousal";
export { survivorBenefit, survivorOptimizationHint } from "./survivor";
export type * from "./types";
